// UnsentUpdatesBar — the office's comments not emailed yet, and Send update.
//
// v0.267.0 (2026-10-29). Brett: "Right now i want to type in a comment for 5
// points. I dont want them to get 5 emails." then "Yes build send update. We
// will need a check when we leave page to ask us if we try to leave with
// unspent messages. It should also leave an indicator when I go back in to say
// if I have unspent messages."
//
// A comment typed under an item is saved and NOT emailed. This strip, under
// the header on every page of a project, says how many are waiting and for how
// many people, and Send update emails each person on the project's links ONE
// email with all of theirs (api/report-updates). It persists until they are
// sent, so coming back to the project shows it again.
//
// Leaving the project with comments unsent asks first, in the app (Send update
// and leave / Leave without sending / Stay); closing or reloading the tab gets
// the browser's own "leave site?" question. Moving between the project's own
// pages (plan, register, shares) does not ask — the strip goes with you.
//
// The count is read when the project opens, when a comment is saved
// (droppin:comment-posted, from DefectConversation), when the app comes back
// to the front and after a send. No timer: the database pauses after an hour
// on purpose and a timer would keep waking it.
//
// Every state is named: not known yet (nothing shown), none waiting (nothing
// shown), waiting, waiting but the database change is not run yet (says so,
// no button), sending, sent/failed per person, and a read that failed (says
// so — a strip that silently vanished would read as "all sent").
//
// v0.323.0 (2026-11-04) - Brett: "the system is live so messages should go
// after i push send. Send update is only for if i have paused sending and
// updated multiple items, then i need to send the update." He chose a
// 1-minute hold. A message the office posts is emailed by itself about a
// minute later: this strip asks the server { auto: true } once the hold has
// passed (the server says when, nextDueMs), so several messages typed in that
// minute go as ONE email, and anything the person read on screen meanwhile is
// not emailed at all. Put the app away, close it or leave the project before
// then and what is waiting goes at once (keepalive), never lost to a closed
// tab. The one timer is that wait: set only when the server says a message is
// in the hold (the database is awake then anyway), never on an interval.
//
// While sending is paused the messages are HELD: the strip counts them and
// Send update releases them (both chats) and emails them at once.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router';
import { Send } from 'lucide-react';
import { apiGet } from '../api';
import { theme, btnPrimary, btnSecondary } from '../theme';
import { CHAT_CHOICES } from '../utils/shareTeam.js';

export const COMMENT_POSTED_EVENT = 'droppin:comment-posted';

function plural(n, one, many) { return `${n} ${n === 1 ? one : many}`; }

// POST straight to the network, never through apiSend: apiSend queues a write
// it cannot deliver, and a Send update replayed from the outbox hours later is
// an email nobody pressed for. v0.315.0 - the Share box's tick sends with it.
// v0.323.0 - { auto } the strip's own send after the hold (only people whose
// messages are all past it); { release } Send update while paused (the held
// messages go too); { keepalive } the send when the app is put away, which
// must outlive the page.
export async function postSendUpdate(projectId, { auto = false, release = false, keepalive = false } = {}) {
  let r;
  try {
    r = await fetch('/api/report-updates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId: Number(projectId), ...(auto ? { auto: true } : {}), ...(release ? { release: true } : {}) }),
      ...(keepalive ? { keepalive: true } : {}),
    });
  } catch (_) {
    return { error: 'No connection — nothing was sent. Send update needs a connection.' };
  }
  let body = null;
  try { body = await r.json(); } catch (_) { /* not JSON */ }
  if (!r.ok) return { error: (body && body.error) || `Send update failed (${r.status}).`, status: r.status };
  return body || { results: [], sent: 0, failed: 0 };
}

export default function UnsentUpdatesBar({ projectId }) {
  const [summary, setSummary] = useState(null);   // null = not known yet
  const [readErr, setReadErr] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState(null);   // { results, sent, failed } | { error }
  const sendingRef = useRef(false);
  // v0.323.0 - the hold: the one wait for the soonest message to pass it.
  const holdRef = useRef(null);         // the timeout, or null = nothing in the hold
  const leaveQuietRef = useRef(false);  // "Leave without sending" - no send on the way out
  const autoRef = useRef(null);         // the auto send, for the timeout to call

  const clearHold = () => { if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; } };
  // The server says when the soonest message in the hold is due; ask just after.
  const scheduleHold = (ms) => {
    clearHold();
    if (ms == null || !Number.isFinite(Number(ms))) return;
    holdRef.current = setTimeout(() => { holdRef.current = null; if (autoRef.current) autoRef.current(); }, Math.max(0, Number(ms)) + 1500);
  };

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const s = await apiGet(`/api/report-updates?projectId=${projectId}`, { fresh: true });
      setSummary(s && typeof s === 'object' ? s : null);
      setReadErr('');
      scheduleHold(s && s.nextDueMs);
    } catch (e) {
      // A signed-out or ended session is the shell's business, not this strip's.
      if (e && (e.status === 401 || e.status === 403)) { setSummary(null); setReadErr(''); return; }
      setReadErr('Could not check for comments not emailed or seen yet.');
      console.warn('[DropPin] unsent updates read failed:', (e && e.message) || e);
    }
  }, [projectId]);

  // v0.323.0 - the hold has passed: send whoever is due, then look again
  // (which waits for anyone still in it). A failure stays on the strip.
  const autoSend = useCallback(async () => {
    if (!projectId) return;
    if (sendingRef.current) { scheduleHold(3000); return; }
    sendingRef.current = true;
    try {
      const res = await postSendUpdate(projectId, { auto: true });
      if (res && (res.error || res.failed)) setOutcome(res);
    } finally {
      sendingRef.current = false;
    }
    await load();
  }, [projectId, load]);
  autoRef.current = autoSend;

  // v0.323.0 - put away, closed or left with a message in the hold: it goes
  // now, by a request that outlives the page. Nothing in the hold, nothing sent.
  const flushHold = useCallback(() => {
    if (!holdRef.current || !projectId) return;
    clearHold();
    if (leaveQuietRef.current) return;
    postSendUpdate(projectId, { keepalive: true });
  }, [projectId]);

  useEffect(() => {
    setSummary(null); setReadErr(''); setConfirming(false); setOutcome(null);
    leaveQuietRef.current = false;
    load();
    return () => flushHold();   // leaving the project, or another project opened
  }, [load, flushHold]);

  useEffect(() => {
    // v0.323.0 - { now } (Resume sending): what was released goes at once.
    const onPosted = (e) => {
      if (e && e.detail && e.detail.now && projectId && !sendingRef.current) {
        clearHold();
        sendingRef.current = true;
        postSendUpdate(projectId)
          .then((res) => { if (res && (res.error || res.failed)) setOutcome(res); })
          .finally(() => { sendingRef.current = false; load(); });
        return;
      }
      load();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
      else flushHold();
    };
    const onPageHide = () => flushHold();
    window.addEventListener(COMMENT_POSTED_EVENT, onPosted);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener(COMMENT_POSTED_EVENT, onPosted);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [load, flushHold, projectId]);

  const count = (summary && summary.comments) || 0;
  const people = (summary && Array.isArray(summary.people)) ? summary.people : [];
  // v0.283.0 - one email per person per report per chat, so a person can be on
  // the list twice; "for n people" counts addresses.
  const persons = new Set(people.map((p) => String(p.recipientEmail || '').toLowerCase())).size;
  const ready = !!(summary && summary.ready);
  // v0.301.0 - the company's own sign-ins are left out (Brett: "don't count our
  // own addresses"); when they could not be read, everyone is counted and the
  // strip says so.
  const ownUnchecked = !!(summary && summary.ownChecked === false);
  const ownLeftOut = (summary && summary.ownLeftOut) || 0;
  // v0.323.0 - held by Pause sending: they wait for Send update.
  const held = (summary && summary.held) || 0;
  const waiting = count > 0 || held > 0;

  // Leaving the project with comments waiting — asked in the app.
  const inThisProject = (path) => path === `/projects/${projectId}` || String(path || '').startsWith(`/projects/${projectId}/`);
  const blocker = useBlocker(({ nextLocation }) => waiting && !sendingRef.current && !inThisProject(nextLocation.pathname));

  // Closing or reloading the tab — the browser asks (it allows no wording of ours).
  useEffect(() => {
    if (!waiting) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [waiting]);

  const send = async () => {
    if (sendingRef.current) return null;
    sendingRef.current = true;
    setSending(true);
    setOutcome(null);
    clearHold();
    try {
      const res = await postSendUpdate(projectId, { release: held > 0 });
      setOutcome(res);
      setConfirming(false);
      await load();
      return res;
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const sendAndLeave = async () => {
    const res = await send();
    // Leave only when every email went; otherwise stay and show who missed out.
    if (res && !res.error && !res.failed) { if (blocker.state === 'blocked') blocker.proceed(); }
    else if (blocker.state === 'blocked') blocker.reset();
  };

  const results = outcome && Array.isArray(outcome.results) ? outcome.results : [];
  const showStrip = waiting || readErr || outcome;
  if (!showStrip && blocker.state !== 'blocked') return null;

  return (
    <>
      {showStrip && (
        <div
          role="status"
          data-testid="unsent-updates"
          style={{
            background: theme.bg, borderBottom: `1px solid ${theme.border}`, color: theme.text,
            padding: '8px 16px', fontSize: 13,
          }}
        >
          <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {waiting && (
              <>
                <Send size={16} aria-hidden="true" style={{ color: theme.brand, flexShrink: 0 }} />
                <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                  {count > 0 && (
                    <>
                      <strong>{plural(count, 'comment', 'comments')} not emailed or seen yet</strong>
                      {' '}— for {plural(persons, 'person', 'people')} on this project's links.
                    </>
                  )}
                  {held > 0 && (
                    <span data-testid="unsent-held">
                      {count > 0 ? ' ' : ''}<strong>{plural(held, 'message', 'messages')} held</strong> while sending is paused — they go when you press Send update.
                    </span>
                  )}
                  {ownUnchecked && " Your company's sign-ins could not be checked, so they are counted too."}
                  {!ready && ' Send update is waiting on a database change.'}
                </span>
                {ready && !confirming && (
                  <button type="button" data-testid="send-update" disabled={sending}
                    onClick={() => { setOutcome(null); setConfirming(true); }}
                    style={{ ...btnPrimary, padding: '5px 12px', fontSize: 12 }}>
                    Send update
                  </button>
                )}
              </>
            )}
            {!waiting && readErr && (
              <span style={{ flex: '1 1 auto', color: theme.textLight }}>{readErr}</span>
            )}
            {!waiting && !readErr && outcome && !outcome.error && (
              <span style={{ flex: '1 1 auto' }}>All comments sent ✓</span>
            )}
            {!waiting && outcome && (
              <button type="button" onClick={() => setOutcome(null)}
                style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }}>
                OK
              </button>
            )}
          </div>

          {confirming && waiting && (
            <div data-testid="send-update-confirm" style={{ maxWidth: 1200, margin: '8px auto 0' }}>
              {held > 0 && (
                <div data-testid="send-update-release" style={{ marginBottom: 6 }}>
                  Sending resumes on this project and the {plural(held, 'held message', 'held messages')} {held === 1 ? 'goes' : 'go'} on their report and by email now.
                </div>
              )}
              {people.length > 0 && <div style={{ marginBottom: 6 }}>One email each, listing their comments:</div>}
              <PeopleList people={people} />
              {ownLeftOut > 0 && (
                <div data-testid="own-left-out" style={{ marginTop: 6, fontSize: 12, color: theme.textLight }}>
                  Your company's own addresses (its sign-ins and anyone at their email domain) are not counted or emailed.
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <button type="button" data-testid="send-update-go" disabled={sending} onClick={send}
                  style={{ ...btnPrimary, padding: '5px 12px', fontSize: 12, opacity: sending ? 0.6 : 1 }}>
                  {sending ? 'Sending…' : held > 0 ? 'Send update' : `Send ${plural(people.length, 'email', 'emails')}`}
                </button>
                <button type="button" disabled={sending} onClick={() => setConfirming(false)}
                  style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {outcome && <Outcome outcome={outcome} results={results} />}
        </div>
      )}

      {blocker.state === 'blocked' && (
        <div
          data-testid="leave-check"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="leave-check-title"
            style={{ background: theme.surface, color: theme.text, width: 460, maxWidth: '100%', borderRadius: 10, padding: 20 }}>
            <h2 id="leave-check-title" style={{ margin: '0 0 8px', fontSize: 17 }}>Comments not emailed or seen yet</h2>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: theme.textLight }}>
              {count > 0 && `${plural(count, 'comment', 'comments')} on this project ${count === 1 ? 'has' : 'have'} not been emailed to, or seen by, the people on its links.`}
              {held > 0 && `${count > 0 ? ' ' : ''}${plural(held, 'message', 'messages')} ${held === 1 ? 'is' : 'are'} held while sending is paused.`}
              {!ready && ' Send update is waiting on a database change, so it cannot send yet.'}
            </p>
            {outcome && <Outcome outcome={outcome} results={results} />}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {ready && (
                <button type="button" data-testid="leave-send" disabled={sending} onClick={sendAndLeave}
                  style={{ ...btnPrimary, opacity: sending ? 0.6 : 1 }}>
                  {sending ? 'Sending…' : 'Send update and leave'}
                </button>
              )}
              <button type="button" data-testid="leave-anyway" disabled={sending} onClick={() => { leaveQuietRef.current = true; clearHold(); blocker.proceed(); }}
                style={btnSecondary}>
                Leave without sending
              </button>
              <button type="button" data-testid="leave-stay" disabled={sending} onClick={() => blocker.reset()}
                style={btnSecondary}>
                Stay
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// The dashboard line (Projects): every project with comments not emailed or seen yet,
// each a button into it — where the strip above takes over. Read once when
// the page opens; nothing shown when none are waiting.
export function UnsentUpdatesNotice({ onOpen }) {
  const [summary, setSummary] = useState(null);
  const [readErr, setReadErr] = useState('');

  useEffect(() => {
    let live = true;
    apiGet('/api/report-updates', { fresh: true })
      .then((s) => { if (live) setSummary(s && typeof s === 'object' ? s : null); })
      .catch((e) => {
        if (!live || (e && (e.status === 401 || e.status === 403))) return;
        setReadErr('Could not check for comments not emailed or seen yet.');
        console.warn('[DropPin] unsent updates read failed:', (e && e.message) || e);
      });
    return () => { live = false; };
  }, []);

  if (readErr) {
    return <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 12 }}>{readErr}</div>;
  }
  const projects = (summary && Array.isArray(summary.projects)) ? summary.projects.filter((p) => p.comments > 0) : [];
  if (!projects.length) return null;
  const total = projects.reduce((n, p) => n + p.comments, 0);

  return (
    <div data-testid="unsent-updates-notice" style={{ padding: '10px 12px', background: theme.bg, border: `1px solid ${theme.brand}`, color: theme.text, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <Send size={16} aria-hidden="true" style={{ color: theme.brand, flexShrink: 0, marginTop: 1 }} />
        <span style={{ flex: '1 1 0', minWidth: 0 }}>
          <strong>{plural(total, 'comment', 'comments')} not emailed or seen yet</strong>
          {' '}on {plural(projects.length, 'project', 'projects')} — open one and press Send update.
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {projects.map((p) => (
          <button key={p.projectId} type="button" onClick={() => onOpen(p.projectId)}
            style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }}>
            {p.projectName || `Project ${p.projectId}`} · {p.comments}
          </button>
        ))}
      </div>
    </div>
  );
}

// v0.283.0 - the chat is named on every row once any link is not the client's.
const chatLabel = (chat) => (CHAT_CHOICES.find((c) => c.chat === chat) || CHAT_CHOICES[0]).label;
const anyOtherChat = (rows) => rows.some((r) => r.chat && r.chat !== 'client');

function PeopleList({ people }) {
  const named = anyOtherChat(people);
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
      {people.map((p) => (
        <li key={`${p.shareId}-${p.recipientEmail}-${p.chat}`} data-person-chat={p.chat || 'client'} style={{ color: theme.textLight, overflowWrap: 'anywhere' }}>
          <span style={{ color: theme.text }}>{p.recipientEmail}</span>
          {p.reportName ? ` · ${p.reportName}` : ''}{named ? ` · ${chatLabel(p.chat)}` : ''} · {plural(p.count, 'comment', 'comments')}
        </li>
      ))}
    </ul>
  );
}

function Outcome({ outcome, results }) {
  if (outcome.error) {
    return (
      <div data-testid="send-update-error" style={{ maxWidth: 1200, margin: '8px auto 0', color: theme.danger }}>
        {outcome.error}
      </div>
    );
  }
  if (!results.length) {
    return (
      <div style={{ maxWidth: 1200, margin: '8px auto 0', color: theme.textLight }}>
        Nothing to send — no one on this project's links has comments waiting.
      </div>
    );
  }
  const named = anyOtherChat(results);
  return (
    <ul data-testid="send-update-results" style={{ maxWidth: 1200, margin: '8px auto 0', padding: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
      {results.map((r, i) => (
        <li key={i} data-result-chat={r.chat || 'client'} style={{ overflowWrap: 'anywhere', color: r.sent ? theme.text : theme.danger }}>
          {r.sent ? '✓ ' : '✗ '}{r.recipientEmail}{named ? ` · ${chatLabel(r.chat)}` : ''} — {r.sent
            ? `sent ${plural(r.count, 'comment', 'comments')}`
            : `not sent (${r.error || 'unknown'}); still waiting, try Send update again`}
        </li>
      ))}
    </ul>
  );
}
